import {
  ArrowDownWideNarrow,
  BookOpenText,
  CalendarArrowDown,
  ChevronsUp,
  CircleCheck,
  CircleDot,
  Eye,
  Layers,
  MessageSquareDot,
  UserRoundCheck
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'ui/components/Button';
import DropdownMenu from 'ui/components/Dropdown';
import { getPageUrl } from 'utils/proxyUtils';
import { useAiReviewTasks } from '../../../hooks/use-ai-review-tasks';
import { usePermissions } from '../../../hooks/use-permissions';
import { useTasks } from '../../../hooks/use-tasks';
import {
  getIsCompleteTasksFilterActive,
  getIsInternalTasksFilterActive,
  getNotifiedUsersFilter,
  getOrderByFilter,
  getPageFilter,
  PageFilterType,
  setNotifiedUsers,
  setOrderBy,
  setPageFilter,
  toggleCompleteTasksFilter,
  toggleInternalTasksFilter,
} from '../../../store/filtersSlice';
import { getSelectedSiteCollaborateInfo, getSelectedSiteInfo } from '../../../store/siteInfoSlice';
import { setCreateModalOpen, setSelectedTaskId } from '../../../store/tasksSlice';
import { getUser } from '../../../store/userSlice';

export const FilterList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isPermitted } = usePermissions();
  const currentPageUrl = getPageUrl() || '';
  const pageFilter = useSelector(getPageFilter);
  const notifiedUsers = useSelector(getNotifiedUsersFilter);
  const orderBy = useSelector(getOrderByFilter);
  const userInfo = useSelector(getUser);
  const siteInfo = useSelector(getSelectedSiteInfo);
  const isCompleteFilterActive = useSelector(getIsCompleteTasksFilterActive);
  const isInternalFilterActive = useSelector(getIsInternalTasksFilterActive);

  const { rawData: thisPage } = useTasks({
    siteId: siteInfo?.id as string,
    workspaceId: siteInfo?.workspace_id,
    currentPageUrl,
    forCurrentPageOnly: true,
    skipQuery: !currentPageUrl || !siteInfo?.id
  });

  const { rawData: allTasks } = useTasks({
    siteId: siteInfo?.id as string,
    simpleMode: true,
    skipQuery: !siteInfo?.id
  });

  useAiReviewTasks()

  const collaborateInfo = useSelector(getSelectedSiteCollaborateInfo);

  const handleAssigned = () => {
    dispatch(setNotifiedUsers(notifiedUsers === undefined ? userInfo.id : undefined));
  };

  const hasPermission = useMemo(() => {
    return collaborateInfo?.result?.role === 'admin' || collaborateInfo?.result?.role === 'contributor';
  }, [collaborateInfo?.result?.role]);

  // Use the new toggle actions for cleaner logic
  const handleComplete = () => {
    dispatch(toggleCompleteTasksFilter());
    dispatch(setCreateModalOpen(false));
    dispatch(setSelectedTaskId(null));
  };

  const handleCurrentPage = (filter: PageFilterType) => {
    dispatch(setPageFilter(filter));
  };

  const handleInternal = () => {
    dispatch(toggleInternalTasksFilter());
  };

  const handleSort = (value: "created_at" | "task_priority" | "is_unread" | "task_status") => {
    dispatch(setOrderBy(value));
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className=""
          toolTipPortal
          tooltip={t('filter_list.tooltip', 'Sort and filter tasks to customize your view')}
        >
          <ArrowDownWideNarrow size={18} />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" className="min-w-[14rem]" portal={false}>
        <DropdownMenu.CheckboxItem
          onClick={() => handleCurrentPage('current')}
          checked={pageFilter === 'current'}
        >
          <BookOpenText size={18} /> {t('filter_list.this_page', 'This Page')}
          <span className='inline-flex items-center  text-center text-xs border-transparent bg-destructive text-white font-normal px-1 leading-4 rounded-[20px] font-medium'>


            {thisPage?.total_records || 0}
          </span>
        </DropdownMenu.CheckboxItem>
        <DropdownMenu.CheckboxItem
          onClick={() => handleCurrentPage('all')}
          checked={pageFilter === 'all'}
        >
          <Layers size={18} /> {t('filter_list.all_pages', 'All Pages')}
          <span className='inline-flex items-center  text-center text-xs border-transparent bg-destructive text-white font-normal px-1 leading-4 rounded-[20px] font-medium'>
            {allTasks?.total_records || 0}
          </span>
        </DropdownMenu.CheckboxItem>
        <DropdownMenu.CheckboxItem
          onClick={handleAssigned}
          checked={notifiedUsers === userInfo.id}
        >
          <UserRoundCheck size={18} /> {t('filter_list.assigned_to_me', 'Assigned To Me')}
          <span className='inline-flex items-center  text-center text-xs border-transparent bg-destructive text-white font-normal px-1 leading-4 rounded-[20px] font-medium'>
            {allTasks?.total_records_assigned_to_me || 0}
          </span>
        </DropdownMenu.CheckboxItem>
        <DropdownMenu.Separator />
        <DropdownMenu.CheckboxItem
          onClick={handleComplete}
          checked={isCompleteFilterActive}
        >
          <CircleCheck size={18} />
          {t('filter_list.show_complete', 'Show Complete Tasks')}
        </DropdownMenu.CheckboxItem>
        {hasPermission && isPermitted("collaborate-sidebar-filters") && (
          <DropdownMenu.CheckboxItem
            onClick={handleInternal}
            checked={isInternalFilterActive}
          >
            <Eye size={18} />
            {t('filter_list.show_internal', 'Show Internal Tasks')}
          </DropdownMenu.CheckboxItem>
        )}
        <DropdownMenu.Separator />
        <DropdownMenu.RadioGroup
          value={orderBy || ''}
          onValueChange={(value) => handleSort(value as "created_at" | "task_priority" | "is_unread" | "task_status")}
        >
          <DropdownMenu.RadioItem
            value="created_at"
            className="gap-2 px-3"
          >

            <CalendarArrowDown size={18} />
            {t('filter_list.sort_date', 'Sort by Date')}
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem
            value="task_priority"
            className="gap-2 px-3"
          >

            <ChevronsUp size={18} />
            {t('filter_list.sort_priority', 'Sort by Priority')}
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem
            value="is_unread"
            className="gap-2 px-3"
          >

            <MessageSquareDot size={18} />
            {t('filter_list.sort_unread', 'Sort by Unread')}
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem
            value="task_status"
            className="gap-2 px-3"
          >
            <CircleDot size={18} />
            {t('filter_list.sort_status', 'Sort by Status')}
          </DropdownMenu.RadioItem>
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};